import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query"
import axios from "config/axios"
import {refetchOnWindowFocus} from "config/reactQuery"
import {useApiDefaults} from "hooks/useApiDefaults"
import {ReactQueryKeys} from "config/constant"

export const OperationTypes = {
  GET: "Get",
  ADD: "Add",
  EDIT: "Edit",
  DELETE: "Delete",
  LIST: "List",
}

export const TIMEOUT = 3000
export const SUCCESS_TIMEOUT = 1500

// Common parameters: RSAClientId, ClientStoreId, member_number, user_token, operation
// Operation will be Get, Add, Edit, Delete, List
// Operation Add: CardNumber, CardType
// Operation Edit: CurrentCardId, CurrentCardNumber, NewCardNumber, CardType
// Operation Delete: CardId
// Operation Get: CardId
// Operation List: No additional parameters
// API Name: AogAdditionalCard
// "CardType":"Fuel",

function showMessage(type, message, setMessage, timeout = TIMEOUT) {
  setMessage({type, message})
  setTimeout(() => {
    setMessage({type: "", message: ""})
  }, timeout)
}

export const useFuelCard = () => {
  const apiDefaults = useApiDefaults()
  delete apiDefaults.PageNo

  const queryClient = useQueryClient()

  const fetcher = async () => {
    const res = await axios.post("/AogAdditionalCard", {
      ...apiDefaults,
      operation: OperationTypes.LIST,
    })
    return res?.data?.data?.AdditionalCards || []
  }

  const queryResult = useQuery({
    queryKey: [ReactQueryKeys.FUEL_CARD, apiDefaults],
    queryFn: fetcher,
    refetchOnWindowFocus,
  })

  const saveMutation = useMutation({
    mutationFn: async ({data, setMessage, successCallback}) => {
      try {
        const res = await axios.post("/AogAdditionalCard", {
          ...apiDefaults,
          ...data,
        })
        const queryKey = [ReactQueryKeys.FUEL_CARD, apiDefaults]
        if (res.data.status === "success") {
          queryClient.invalidateQueries({
            queryKey,
          })

          successCallback?.()

          let messageText = ""
          if (data.operation === OperationTypes.ADD) {
            messageText = "Card added successfully"
            queryClient.setQueryData(queryKey, oldData => {
              return [...oldData, data]
            })
          } else if (data.operation === OperationTypes.EDIT) {
            messageText = "Card updated successfully"
            queryClient.setQueryData(queryKey, oldData => {
              const newData = oldData.find(
                card => card.CardId === data.CurrentCardId
              )
              if (newData) {
                newData.CardNumber = data.NewCardNumber
              }
              return oldData
            })
          } else if (data.operation === OperationTypes.DELETE) {
            messageText = "Card deleted successfully"
            queryClient.setQueryData(queryKey, oldData => {
              return oldData.filter(card => card.CardId !== data.CardId)
            })
          }
          showMessage("success", messageText, setMessage, SUCCESS_TIMEOUT)
        } else {
          showMessage("error", res.data.message, setMessage)
        }
      } catch (error) {
        showMessage(
          "error",
          error.message || "Something went wrong",
          setMessage
        )
      }
    },
  })

  return {...queryResult, saveMutation}
}
